import { useEffect, useId, useRef, useState } from "react";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";

import {
    canPlaceAt,
    computeFinishIndex,
    isDragData,
    isDropData,
} from "../utils";

interface UseListMonitorArgs<T> {
    items: ReadonlyArray<T>;
    onReorder: (newItems: Array<T>) => void;
}

interface UseListMonitorResult {
    listId: string;
    // `true` only on the render that applies a drop, so the list can skip its reorder animation.
    isDndReordering: boolean;
}

export function useListMonitor<T>({
    items,
    onReorder,
}: UseListMonitorArgs<T>): UseListMonitorResult {
    const listId = useId();
    const [isDndReordering, setIsDndReordering] = useState(false);

    // Mirror items/onReorder so the monitor reads the latest values without
    // resubscribing on every render.
    const itemsRef = useRef(items);
    // eslint-disable-next-line react-hooks/refs
    itemsRef.current = items;
    const onReorderRef = useRef(onReorder);
    // eslint-disable-next-line react-hooks/refs
    onReorderRef.current = onReorder;

    // Subscribe one monitor per list instance: commit the reorder when a drag
    // sourced from this list drops on a valid target belonging to the same list.
    useEffect(() => {
        return monitorForElements({
            canMonitor: ({ source }) =>
                isDragData(source.data) && source.data.listId === listId,
            onDrop: ({ source, location }) => {
                const destination = location.current.dropTargets[0];
                if (
                    !destination ||
                    !isDropData(destination.data) ||
                    !isDragData(source.data)
                ) {
                    return;
                }
                if (!canPlaceAt(source.data, destination.data)) {
                    return;
                }
                setIsDndReordering(true);
                onReorderRef.current(
                    reorder({
                        list: [...itemsRef.current],
                        startIndex: source.data.index,
                        finishIndex: computeFinishIndex(
                            source.data.index,
                            destination.data.index,
                            destination.data.edge
                        ),
                    })
                );
            },
        });
    }, [listId]);

    // Clears the flag once items commit
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsDndReordering(false);
    }, [items]);

    return { listId, isDndReordering };
}
